/**
 * Helpers for working with a contenteditable div as a "rich text input".
 *
 * The text representation is the canonical form used for trigger detection
 * and submission. Inside the DOM:
 *
 *   - Plain text nodes count as their textContent
 *   - <br> counts as "\n"
 *   - <img data-shortcode="foo"> counts as ":foo:" (custom emoji)
 *   - <span data-user-id="42">@Alice Andersen</span> counts as its
 *     own textContent ("@Alice Andersen") for trigger detection; the
 *     id is recovered separately by getRaw(), which writes `@[42]`.
 *
 * Caret offsets are integer positions in this text representation.
 */

const NL = "\n";

/** Walk a DOM subtree and produce its text representation. */
export function getTextRepresentation(root: Node): string {
  let text = "";
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? "";
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.tagName === "IMG" && el.dataset.shortcode) {
      text += `:${el.dataset.shortcode}:`;
      return;
    }
    if (el.tagName === "BR") {
      text += NL;
      return;
    }
    // Block elements: process children, but treat the boundary as a newline
    // if browsers wrap multiline contenteditable content in <div>/<p>.
    const isBlock = el.tagName === "DIV" || el.tagName === "P";
    if (isBlock && text && !text.endsWith(NL)) text += NL;
    for (const child of Array.from(node.childNodes)) walk(child);
  };
  for (const child of Array.from(root.childNodes)) walk(child);
  return text;
}

/** Get the caret offset within the text representation. */
export function getCaretOffsetInText(root: HTMLElement): number {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return 0;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.endContainer)) return 0;
  return offsetToTarget(root, range.endContainer, range.endOffset);
}

/** Walk root and count text-representation chars up to (target, offset). */
function offsetToTarget(root: Node, target: Node, targetOffset: number): number {
  let offset = 0;
  let found = false;

  const walk = (node: Node): void => {
    if (found) return;

    if (node === target) {
      if (node.nodeType === Node.TEXT_NODE) {
        offset += targetOffset;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // The selection is between children of `target` — count children before targetOffset.
        const children = Array.from(node.childNodes).slice(0, targetOffset);
        for (const c of children) walk(c);
      }
      found = true;
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      offset += node.textContent?.length ?? 0;
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;

    if (el.tagName === "IMG" && el.dataset.shortcode) {
      offset += `:${el.dataset.shortcode}:`.length;
      return;
    }
    if (el.tagName === "BR") {
      offset += 1;
      return;
    }

    // Block elements (div/p) are treated as containers; their newline
    // separation is handled by the user inserting <br> or by us inserting
    // newlines when pasting. We don't add implicit newlines here to keep
    // the offset math symmetric with insertion code.
    for (const child of Array.from(node.childNodes)) walk(child);
  };

  for (const child of Array.from(root.childNodes)) walk(child);
  return offset;
}

/** Place the caret at a given text-representation offset within root. */
export function setCaretOffsetInText(root: HTMLElement, target: number): void {
  const result = findDomPositionAtOffset(root, target);
  if (!result) return;
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.setStart(result.node, result.offset);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * Resolve a text-rep offset to a (DOM node, offset) pair.
 *
 * Exported for `lib/richText/editorDom.ts#replaceTextRangeWithFragment`, which
 * is its only caller outside this file — `RichTextInput` reaches it through
 * that helper, not directly.
 */
export function findDomPositionAtOffset(
  root: HTMLElement,
  target: number
): { node: Node; offset: number } | null {
  let consumed = 0;
  let result: { node: Node; offset: number } | null = null;

  const walk = (node: Node): boolean => {
    if (result) return true;

    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.textContent?.length ?? 0;
      if (consumed + len >= target) {
        result = { node, offset: target - consumed };
        return true;
      }
      consumed += len;
      return false;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    const el = node as HTMLElement;

    if (el.tagName === "IMG" && el.dataset.shortcode) {
      const len = `:${el.dataset.shortcode}:`.length;
      if (consumed + len >= target) {
        // Place caret right after this image
        const parent = el.parentNode!;
        const idx = Array.from(parent.childNodes).indexOf(el) + 1;
        result = { node: parent, offset: idx };
        return true;
      }
      consumed += len;
      return false;
    }

    if (el.tagName === "BR") {
      if (consumed + 1 >= target) {
        const parent = el.parentNode!;
        const idx = Array.from(parent.childNodes).indexOf(el) + 1;
        result = { node: parent, offset: idx };
        return true;
      }
      consumed += 1;
      return false;
    }

    for (const child of Array.from(node.childNodes)) {
      if (walk(child)) return true;
    }
    return false;
  };

  for (const child of Array.from(root.childNodes)) {
    if (walk(child)) break;
  }

  if (!result) {
    // Past the end → place at end of root
    result = { node: root, offset: root.childNodes.length };
  }
  return result;
}
